/**
 * Sezione Gestione Stock - Solo Seller con estensione attiva
 */
import React, { useState, useEffect } from 'react';
import { getErrorMessage } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  Boxes, 
  Save,
  Loader2,
  Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StockSection = ({ user, t, isExtensionActive }) => {
  const [stockSettings, setStockSettings] = useState({
    stock_enabled: false,
    low_stock_threshold: 10,
    stock_display_mode: 'available_only',
    stock_allow_backorder: true,
    stock_reserve_on_order: true,
    auto_update_from_erp: false,
    erp_sync_interval: 60
  });
  const [savingStock, setSavingStock] = useState(false);

  const extActive = isExtensionActive('ext_stock');

  useEffect(() => {
    if (user?.role === 'seller' && extActive) {
      fetchStockSettings();
    }
  }, [user, extActive]);

  const fetchStockSettings = async () => {
    try {
      const res = await axios.get(`${API}/stock/settings`);
      setStockSettings(res.data);
    } catch (error) {
      console.error('Error fetching stock settings:', error);
    }
  };

  const handleSaveStockSettings = async () => {
    setSavingStock(true);
    try {
      await axios.put(`${API}/stock/settings`, stockSettings);
      toast.success('Impostazioni stock salvate');
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSavingStock(false);
    }
  };

  if (user?.role !== 'seller') return null;

  return (
    <Card 
      className={!extActive ? 'opacity-75' : ''}
      data-testid="stock-section"
    >
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Boxes className="h-5 w-5" />
          {t('settings.stockManagement')}
          {!extActive && (
            <Badge variant="secondary" className="ml-2">
              <Lock className="h-3 w-3 mr-1" />
              Estensione non attiva
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          {t('settings.stockManagementDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!extActive ? (
          <div className="text-center py-8 text-muted-foreground">
            <Boxes className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Attiva l&apos;estensione &quot;Magazzino&quot; per gestire le giacenze</p>
            <p className="text-sm mt-2">Vai alla sezione Estensioni per attivare il trial gratuito</p>
          </div>
        ) : (
          <>
            {/* Abilita/Disabilita Stock */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
              <div>
                <Label className="text-base">{t('settings.enableStock')}</Label>
                <p className="text-sm text-muted-foreground">
                  {t('settings.enableStockDescription')}
                </p>
              </div>
              <Switch
                checked={stockSettings.stock_enabled}
                onCheckedChange={(checked) => setStockSettings({ ...stockSettings, stock_enabled: checked })}
                data-testid="stock-enabled-switch"
              />
            </div>

            {stockSettings.stock_enabled && (
              <>
                {/* Soglia Stock Basso */}
                <div className="space-y-2">
                  <Label>{t('settings.lowStockThreshold')}</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number"
                      min={1}
                      max={1000}
                      value={stockSettings.low_stock_threshold}
                      onChange={(e) => setStockSettings({ ...stockSettings, low_stock_threshold: parseInt(e.target.value) || 10 })}
                      className="w-24"
                      data-testid="low-stock-threshold-input"
                    />
                    <span className="text-sm text-muted-foreground">unità</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Riceverai una notifica quando un prodotto scende sotto questa soglia
                  </p>
                </div>

                {/* Modalità Visualizzazione */}
                <div className="space-y-2">
                  <Label>{t('settings.stockDisplayMode')}</Label>
                  <Select
                    value={stockSettings.stock_display_mode}
                    onValueChange={(value) => setStockSettings({ ...stockSettings, stock_display_mode: value })}
                  >
                    <SelectTrigger data-testid="stock-display-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exact">Quantità esatta (es: 42 disponibili)</SelectItem>
                      <SelectItem value="available_only">Solo disponibilità (Disponibile/Esaurito)</SelectItem>
                      <SelectItem value="hidden">Nascosto ai buyer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Opzioni aggiuntive */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>{t('settings.allowBackorder')}</Label>
                      <p className="text-xs text-muted-foreground">
                        Consenti ordini anche per prodotti esauriti
                      </p>
                    </div>
                    <Switch
                      checked={stockSettings.stock_allow_backorder}
                      onCheckedChange={(checked) => setStockSettings({ ...stockSettings, stock_allow_backorder: checked })}
                      data-testid="allow-backorder-switch"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>{t('settings.reserveOnOrder')}</Label>
                      <p className="text-xs text-muted-foreground">
                        Scala automaticamente lo stock quando viene confermato un ordine
                      </p>
                    </div>
                    <Switch
                      checked={stockSettings.stock_reserve_on_order}
                      onCheckedChange={(checked) => setStockSettings({ ...stockSettings, stock_reserve_on_order: checked })}
                      data-testid="reserve-on-order-switch"
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleSaveStockSettings}
                  disabled={savingStock}
                  data-testid="save-stock-settings-btn"
                >
                  {savingStock ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {t('common.save')}
                    </>
                  )}
                </Button>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StockSection;
